import { useMemo } from 'react';

const DHAKA_DATE_FORMATTER = new Intl.DateTimeFormat('en-CA', {
  timeZone: 'Asia/Dhaka',
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
});

/**
 * Compute today's Dhaka calendar date as a YYYY-MM-DD string. Memoised for
 * the lifetime of the component — refresh on a remount, not on every render.
 *
 * Note: this is component-local memoisation, so it can drift if a tab stays
 * open across midnight Dhaka. The entry form is intended for active use, so
 * the trade-off is acceptable; if it becomes a problem, swap in a
 * setInterval that compares the string at hourly granularity.
 */
export function useDhakaToday(): string {
  return useMemo(() => formatDhaka(new Date()), []);
}

export function formatDhaka(date: Date): string {
  const parts = DHAKA_DATE_FORMATTER.formatToParts(date);
  const y = parts.find((p) => p.type === 'year')?.value;
  const m = parts.find((p) => p.type === 'month')?.value;
  const d = parts.find((p) => p.type === 'day')?.value;
  if (!y || !m || !d) throw new Error('Failed to format Dhaka date');
  return `${y}-${m}-${d}`;
}
